/**
 * The version list — `bimgo-ai-web`'s `SkillVersionsPanel.tsx`, read-only.
 *
 * Rows are `v{n}`, a "Published" tag, the date, the commit message; the current
 * version is highlighted. Two modes: without `onSwitch`, clicking a row
 * **views** that version's code (phase 1's read-only list); with it — the
 * editor — clicking a non-current row **switches** the working copy to it, and
 * every row refuses while a regen stream, an approval or another restore is in
 * flight, because a switch racing the eventual commit refresh would flicker
 * the editor between the two outcomes (item BE).
 */
import { useEffect, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { SkillsClient, SkillVersionsPage } from "../../lib/skills";
import { LoaderIcon } from "./icons";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; page: SkillVersionsPage };

/** The reference's `stripCodeBlocks`: a commit message can carry the diff. */
function stripCodeBlocks(text: string): string {
  return text.replace(/```[\s\S]*?```/g, "").trim();
}

export function SkillVersionsPanel({
  client,
  skillId,
  viewing,
  onView,
  onSwitch,
  switchBlocked = false,
  restoringVersion = null,
  refreshKey = 0,
}: {
  client: SkillsClient;
  skillId: string;
  /** The version whose code the Code tab is showing, or null for the working copy. */
  viewing: number | null;
  onView: (version: number | null) => void;
  /**
   * The editor's mode: clicking a non-current row **switches** to it (the
   * reference's behaviour) instead of viewing it. Absent = phase 1's read-only
   * list, which views.
   */
  onSwitch?: (version: number) => void;
  /** A regen stream / approval / restore is mid-flight — rows refuse, with the hint. */
  switchBlocked?: boolean;
  restoringVersion?: number | null;
  /** Bumped when a commit or switch changed the list — triggers a reload. */
  refreshKey?: number;
}) {
  const { t, locale } = useLocale();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => (prev.kind === "loaded" ? prev : { kind: "loading" }));
    client
      .versions(skillId)
      .then((page) => {
        if (!cancelled) setState({ kind: "loaded", page });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ kind: "error", message: error instanceof Error ? error.message : t("skillVersions.loadError") });
      });
    return () => {
      cancelled = true;
    };
  }, [client, skillId, refreshKey]);

  if (state.kind === "loading") {
    return (
      <div className="skills-center muted">
        <LoaderIcon size={14} /> {t("common.loading")}
      </div>
    );
  }
  if (state.kind === "error") return <div className="skills-center err">{state.message}</div>;
  const { page } = state;
  if (page.versions.length === 0) return <div className="skills-center muted">{t("skillVersions.empty")}</div>;

  // Newest first, as the reference lists them.
  const entries = [...page.versions].sort((a, b) => b.version - a.version);

  return (
    <>
      {/**
       * The amber hint, **but not during the switch it is describing** (owner).
       *
       * `switchBlocked` covers everything that makes the rows refuse, and one of those is a
       * restore already running. So pressing a version raised the hint for the second or two
       * the restore took and dropped it again — a block of colour appearing at the top of this
       * column and pushing the list down, then undoing it. That flash is what you notice, not
       * the message.
       *
       * The restore already has its own feedback, on the row being restored (`isRestoring`,
       * the spinner where the date goes). The hint is for the case it cannot speak for: an
       * edit turn or an approval is holding the skill, and the rows are inert for a reason
       * that is nowhere else on screen.
       */}
      {onSwitch && switchBlocked && restoringVersion === null ? (
        <p className="version-blocked-hint">{t("skillVersions.switchBlockedHint")}</p>
      ) : null}
      <ul className="version-rows">
        {entries.map((entry) => {
          const isCurrent = page.current_version === entry.version;
          const isViewing = viewing === entry.version || (viewing === null && isCurrent);
          const isPublished = page.published_version === entry.version;
          const isRestoring = restoringVersion === entry.version;
          const disabled = onSwitch ? isCurrent || switchBlocked : false;
          const activate = () => {
            if (onSwitch) {
              if (!disabled) onSwitch(entry.version);
              return;
            }
            onView(isCurrent ? null : entry.version);
          };
          return (
            <li
              key={entry.version}
              className={`version-item${isCurrent ? " is-current" : ""}${isViewing ? " is-viewing" : ""}${disabled && !isCurrent ? " is-blocked" : ""}`}
              onClick={activate}
              role="button"
              tabIndex={0}
              title={onSwitch && switchBlocked && !isCurrent ? t("skillVersions.switchBlockedHint") : undefined}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") activate();
              }}
            >
              <div className="version-item-head">
                <span className="version-item-main">
                  {/**
                   * **A green dot for the current version, not the word** (owner).
                   *
                   * The row carried up to four things beside the date — the dot, "v12",
                   * PUBLISHED and CURRENT — in a column narrow enough that the two tags and
                   * the date collided, and on a wide window the collision is what you notice
                   * first. The dot was already there as a shape with no meaning of its own, so
                   * it takes the one the word had, and green because "this is the live one" is
                   * the same claim the connected markers make elsewhere.
                   *
                   * The word survives as the dot's accessible name and tooltip, so hovering
                   * still says it and a screen reader still reads it — it is out of the
                   * layout, not out of the interface.
                   */}
                  <span
                    className={`version-dot${isCurrent ? " is-current" : ""}`}
                    {...(isCurrent
                      ? { role: "img", "aria-label": t("skillVersions.current"), title: t("skillVersions.current") }
                      : { "aria-hidden": true })}
                  />
                  <span className="version-item-label">v{entry.version}</span>
                  {isPublished ? <span className="version-item-tag">{t("skillVersions.published")}</span> : null}
                </span>
                <span className="version-item-date">
                  {isRestoring ? <LoaderIcon size={11} /> : null}
                  {formatDate(entry.saved_at, locale)}
                </span>
              </div>
              {entry.message ? <p className="version-item-msg">{stripCodeBlocks(entry.message)}</p> : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function formatDate(iso: string | null, locale: string): string {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(locale === "zh" ? "zh-TW" : "en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
